import { call, put, select } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'
import produce from 'immer'

import { ApplicationState } from 'src/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import { tOperator } from 'src/views/Management/policies/form/utils'
import api from 'src/services/api'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'
import { currencyMask } from 'src/components/Formik'

import { ResponseDataTickets } from './types'
import * as ReceiptsActions from './actions'

export function* removeReceipt({
  payload: { receipt }
}: ReturnType<typeof ReceiptsActions.removeReceipt>) {
  try {
    const {
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    const encodedRequest = produce(receipt, draft => {
      draft.active = false
      draft.request_ticket_receipts[0].active = false
    })

    const response: ApiResponse<ResponseDataTickets> = yield call(
      api.post,
      `/organizations/${organization.organization_id}/requests/${receipt.request_id}/tickets`,
      { request_tickets: [encodedRequest] }
    )

    if (response.ok) {
      yield put(ReceiptsActions.removeReceiptSuccess())
      yield put(ReceiptsActions.setSavedReceipt(null))
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(ReceiptsActions.removeReceiptFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga removeReceipt', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(ReceiptsActions.removeReceiptFailure(message))
  }
}

export function* saveReceipt({
  payload: { receipt }
}: ReturnType<typeof ReceiptsActions.saveReceipt>) {
  try {
    const {
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    const encodedRequest = produce(receipt, draft => {
      draft.request_ticket_id = draft.request_ticket_id
        ? +draft.request_ticket_id
        : null

      draft.request_ticket_number = draft.request_ticket_number
        ? +draft.request_ticket_number
        : null

      draft.request_ticket_receipts = draft.request_ticket_receipts.map(
        item => ({
          ...item,
          currency_id: +item.currency_id,
          payment_resource_id: +item.payment_resource_id,
          reference_id: +item.reference_id,
          request_ticket_receipt_id: item.request_ticket_receipt_id
            ? +item.request_ticket_receipt_id
            : null,
          request_ticket_receipt_number: item.request_ticket_receipt_number
            ? +item.request_ticket_receipt_number
            : null,
          service_id: +item.service_id,
          request_ticket_receipt_images: item.request_ticket_receipt_images.map(
            imageItem => ({
              active: imageItem.active,
              image_url: imageItem.image_url,
              request_ticket_receipt_image_id:
                imageItem.request_ticket_receipt_image_id
                  ? imageItem.request_ticket_receipt_image_id
                  : null,
              request_ticket_receipt_image_number:
                imageItem.request_ticket_receipt_image_number
                  ? imageItem.request_ticket_receipt_image_number
                  : null
            })
          )
        })
      )
      draft.request_ticket_user_data = draft.request_ticket_user_data.map(
        item => ({
          included: item.included,
          owner: item.owner,
          request_ticket_id: item.request_ticket_id
            ? item.request_ticket_id
            : null,
          request_ticket_user_data_id: item.request_ticket_user_data_id
            ? item.request_ticket_user_data_id
            : null,
          user_id: item.traveler ? +item.traveler.user_id : null
        })
      )

      draft.user_id = draft.user_id
        ? +draft.user_id
        : +draft.request_ticket_user_data[0].user_id
    })

    const response: ApiResponse<ResponseDataTickets> = yield call(
      api.post,
      `/organizations/${organization.organization_id}/requests/${receipt.request_id}/tickets`,
      { request_tickets: [encodedRequest] }
    )

    if (response.ok) {
      yield put(ReceiptsActions.saveReceiptSuccess())
      yield put(
        ReceiptsActions.setSavedReceipt(response.data.request_tickets[0])
      )
      return
    }

    if (response.data) {
      const { exception } = response.data
      let message = i18n.t('common.response.unexpected-error')
      if (response.status === 403 && exception === 'PolicyViolated') {
        try {
          const policy_expense_limits =
            response.data.reasons.violated_policy.policy_expense_limits[0]

          let limitText = ''
          let value = ''
          if (policy_expense_limits.daily_limit) {
            //is daily limit
            value = currencyMask({
              formatStr: true,
              precision: 2,
              value: policy_expense_limits.daily_limit
            }).maskedValue
            limitText = i18n.t('web.receipts.policy-violated.daily-limit')
          } else {
            //is normal limit
            value = currencyMask({
              formatStr: true,
              precision: 2,
              value: policy_expense_limits.limit
            }).maskedValue
            limitText = i18n.t('web.receipts.policy-violated.limit')
          }
          let operator = i18n
            .t(tOperator(policy_expense_limits.operator))
            .toLowerCase()

          let action = ''
          if (policy_expense_limits.action === 'ALERT') {
            action = i18n.t('web.receipts.policy-violated.add-justification')
          }

          let preposition = ''
          if (['<=', '>='].includes(policy_expense_limits.operator)) {
            preposition =
              i18n.t('web.receipts.policy-violated.preposition') + ' '
          }

          message = i18n.t('web.receipts.policy-violated.unable-to-save', {
            action,
            limitText,
            message: response.data.reasons.message,
            operator,
            preposition,
            value
          })
          yield put(ReceiptsActions.saveReceiptFailure(message, true))
        } catch (error) {
          yield put(ReceiptsActions.saveReceiptFailure(message, true))
        }
        return
      }

      if (response.status === 405 && exception === 'BudgetViolated') {
        if (typeof response.data.reasons?.message === 'string') {
          message = response.data.reasons.message
        }
        ReceiptsActions.saveReceiptFailure(message, true)
        return
      }

      const { reasons } = response.data
      if (typeof reasons === 'string' && reasons) {
        yield put(
          ReceiptsActions.saveReceiptFailure(
            reasons,
            exception === 'PolicyViolated'
          )
        )
        return
      }
    }

    const message = findBasicApiResponseErrors(response)
    yield put(ReceiptsActions.saveReceiptFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga saveReceipt', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(ReceiptsActions.saveReceiptFailure(message))
  }
}
